import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDebounce } from 'use-debounce'
import {
  type BaseDropdownProps,
  Dropdown,
  type DropdownOption
} from '~/components/Form/Input/Dropdown'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { useDeleteExternalRecipientMutation } from '~/domains/actions/mutations/deleteExternalRecipient'
import {
  addressBookHitValue,
  parseTypedAddress,
  useAddressBookSearch
} from '~/domains/actions/queries/addressBook'
import type {
  AddressBookHit,
  AddressBookType,
  TypedAddress
} from '~/domains/actions/types'
import { AddressBookOption } from './AddressBookOption'

type AddressBookPickerProps = InputInFormProps &
  Pick<BaseDropdownProps, 'inlineList' | 'small' | 'fullWidth'> & {
    name: string
    label?: string
    placeholder?: string
    /** Allow typing a free email address that is not part of the address book. */
    allowFreeText?: boolean
    /** When set, contacts are restricted to this company (order context). */
    orderCompanyId?: number
    types?: AddressBookType[]
    defaultValueOptions?: DropdownOption[]
    onChange?: (values: string[]) => void
  }

const hitToOption = (
  hit: AddressBookHit,
  onDelete?: () => void
): DropdownOption => {
  const name = hit.fullname?.trim() || hit.email
  const displayName = hit.company ? `${name} · ${hit.company}` : name
  return {
    value: addressBookHitValue(hit),
    label: `${displayName} — ${hit.email}`,
    element: (
      <AddressBookOption
        type={hit.type}
        name={displayName}
        email={hit.email}
        onDelete={onDelete}
      />
    )
  }
}

const externalOption = (
  address: TypedAddress,
  create = false
): DropdownOption => {
  const name = address.name ?? address.email
  return {
    value: address.raw,
    label: address.name ? `${address.name} <${address.email}>` : address.email,
    element: (
      <AddressBookOption
        type="external"
        name={create ? `Créer : ${name}` : name}
        email={address.name ? address.email : ''}
      />
    )
  }
}

const selectedFallbackOption = (value: string): DropdownOption => {
  if (!value.startsWith('/')) {
    const parsed = parseTypedAddress(value)
    if (parsed) {
      return externalOption(parsed)
    }
  }
  return { value, label: value }
}

export const AddressBookPicker = ({
  name,
  label,
  placeholder,
  allowFreeText = false,
  orderCompanyId,
  types,
  defaultValueOptions,
  onChange,
  register,
  watch,
  setValue,
  error,
  ...layoutProps
}: AddressBookPickerProps): ReactElement => {
  const [term, setTerm] = useState('')
  const [debouncedTerm] = useDebounce(term, 250)
  // Invalid free-typed address whose selection was rejected: the notice clears
  // as soon as the user edits the term.
  const [rejectedTerm, setRejectedTerm] = useState<string | null>(null)

  const { data: hits = [] } = useAddressBookSearch({
    term: debouncedTerm,
    types,
    orderCompanyId
  })
  const { mutate: deleteExternalRecipient } =
    useDeleteExternalRecipientMutation()
  // Meilisearch removal is async, so optimistically hide forgotten recipients from the list.
  const [forgottenIds, setForgottenIds] = useState<Set<number>>(() => new Set())
  const forgetExternalRecipient = useCallback(
    (id: number) => {
      setForgottenIds((previous) => new Set(previous).add(id))
      deleteExternalRecipient(id)
    },
    [deleteExternalRecipient]
  )

  // Keep labels for values already selected even once they leave the search results.
  const [knownOptions, setKnownOptions] = useState<
    Record<string, DropdownOption>
  >(() => {
    const seed: Record<string, DropdownOption> = {}
    for (const option of defaultValueOptions ?? []) {
      seed[option.value] = option
    }
    return seed
  })

  useEffect(() => {
    if (hits.length === 0) {
      return
    }
    setKnownOptions((previous) => {
      const next = { ...previous }
      for (const hit of hits) {
        const option = hitToOption(hit)
        next[option.value] = option
      }
      return next
    })
  }, [hits])

  const watchedValue = watch?.(name)
  const selectedValues = useMemo<string[]>(() => {
    if (Array.isArray(watchedValue)) {
      return watchedValue
    }
    return defaultValueOptions?.map((option) => option.value) ?? []
  }, [watchedValue, defaultValueOptions])

  const trimmedTerm = term.trim()
  const options = useMemo<DropdownOption[]>(() => {
    const result: DropdownOption[] = []
    const seen = new Set<string>()

    const typedAddress = allowFreeText ? parseTypedAddress(trimmedTerm) : null
    // Don't offer the custom email when an address-book entity already owns that exact email.
    const matchesExistingHit =
      typedAddress !== null &&
      hits.some(
        (hit) => hit.email.toLowerCase() === typedAddress.email.toLowerCase()
      )
    if (
      typedAddress &&
      !matchesExistingHit &&
      !selectedValues.includes(typedAddress.raw)
    ) {
      result.push(externalOption(typedAddress, true))
      seen.add(typedAddress.raw)
    }

    for (const hit of hits) {
      if (hit.type === 'external' && forgottenIds.has(hit.id)) {
        continue
      }
      const value = addressBookHitValue(hit)
      if (!seen.has(value)) {
        // Saved external recipients can be forgotten from the address book straight from the list
        // (only as a suggestion, not once selected as a recipient chip).
        const deletable =
          hit.type === 'external' && !selectedValues.includes(value)
        result.push(
          hitToOption(
            hit,
            deletable ? () => forgetExternalRecipient(hit.id) : undefined
          )
        )
        seen.add(value)
      }
    }

    // While the typed address is still invalid, keep offering the creation (after
    // the hits, so Enter still picks the best match) so the user discovers free
    // typing; selecting it only shows why it is refused.
    if (
      allowFreeText &&
      !typedAddress &&
      trimmedTerm !== '' &&
      !seen.has(trimmedTerm)
    ) {
      result.push({
        value: trimmedTerm,
        label: trimmedTerm,
        element: (
          <AddressBookOption
            type="external"
            name={`Créer : ${trimmedTerm}`}
            email=""
            invalidNotice={
              rejectedTerm === trimmedTerm
                ? 'Adresse email invalide'
                : undefined
            }
          />
        ),
        onSelect: () => {
          setRejectedTerm(trimmedTerm)
          return false
        }
      })
      seen.add(trimmedTerm)
    }

    for (const value of selectedValues) {
      if (!seen.has(value)) {
        result.push(knownOptions[value] ?? selectedFallbackOption(value))
        seen.add(value)
      }
    }

    return result
  }, [
    allowFreeText,
    trimmedTerm,
    rejectedTerm,
    hits,
    selectedValues,
    knownOptions,
    forgottenIds,
    forgetExternalRecipient
  ])

  return (
    <Dropdown
      name={name}
      label={label}
      placeholder={placeholder}
      searchPlaceholder={
        allowFreeText ? 'Rechercher ou saisir une adresse email' : 'Rechercher'
      }
      multiple={true}
      searchable={true}
      searchShouldFilterOptions={false}
      enterSelectsFirstOption={true}
      clearSearchOnSelect={true}
      options={options}
      defaultValueOptions={defaultValueOptions}
      onSearchChange={(search) => setTerm(search ?? '')}
      onChange={onChange}
      register={register}
      watch={watch}
      setValue={setValue}
      error={error}
      {...layoutProps}
    />
  )
}
